import React, { useState, useRef } from 'react';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  FaUser, FaIdCard, FaMapMarkerAlt, FaPhone, FaImage, FaPen, FaSave, FaArrowLeft
} from 'react-icons/fa';

const CATEGORIES = [
  'Farmer', 'Cultivator', 'Trader', 'Researcher', 'Student',
  'NGO Member', 'Government Employee', 'Ayurvedic Practitioner', 'Other'
];

const initialForm = {
  name: '', father_name: '', mother_name: '', spouse_name: '',
  full_address: '', village: '', post: '', hobli: '', taluk: '', district: '', pin: '',
  mobile_phone: '', email: '',
  aadhaar_no: '', pan: '', voter_id: '', ration_card_no: '', driving_license_no: '', passport_no: '',
  category: ''
};

export default function RegistrationForm() {
  const [form, setForm] = useState(initialForm);
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const photoRef = useRef();
  const sigRef = useRef();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSignature = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignature(file);
      setSignaturePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.mobile_phone.trim()) return toast.error('Mobile number is required');
    if (!form.aadhaar_no.trim()) return toast.error('Aadhaar number is required');

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        formData.append(k, v);
      });
      // Admin registration defaults to pending for Super Admin approval
      formData.append('status', 'pending');
      if (photo) formData.append('photo', photo);
      if (signature) formData.append('signature', signature);

      const res = await API.post('/members', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Member registered! ID: ${res.data.memberId}`);
      setSuccess(res.data);
      setForm(initialForm);
      setPhoto(null); setSignature(null);
      setPhotoPreview(null); setSignaturePreview(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setPhoto(null); setSignature(null);
    setPhotoPreview(null); setSignaturePreview(null);
    setSuccess(null);
  };

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>New Member Registration</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>Fill in all details to register a new association member</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/registrations')}>
          <FaArrowLeft size={13} /> All Members
        </button>
      </div>

      <div className="page-content">
        {/* Success Banner */}
        {success && (
          <div style={{
            background: 'linear-gradient(135deg, #15803d, #22c55e)', color: 'white',
            borderRadius: 12, padding: '20px 24px', marginBottom: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>✅ Member Registered Successfully!</div>
              <div style={{ opacity: 0.9, fontSize: 14 }}>
                Member ID: <strong style={{ fontFamily: 'monospace', fontSize: 16 }}>{success.memberId}</strong> — 
                Login: <strong>{success.member?.mobile_phone}</strong> / Last 4 digits
              </div>
            </div>
            <button className="btn" onClick={handleReset} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
              + New Registration
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, background: '#f0fdf4', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaUser color="#16a34a" />
                </div>
                <div>
                  <h3 style={{ fontWeight: 700, fontSize: 16 }}>Member Registration Form</h3>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>Sri Karunasai Medicinal Plantation Association</p>
                </div>
              </div>
            </div>

            <div className="card-body">
              {/* ── Personal Information ── */}
              <div className="form-section-title"><FaUser size={12} /> Personal Information</div>
              <div className="form-grid-2" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" name="name" value={form.name} onChange={handleChange} placeholder="Full name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Father Name</label>
                  <input className="form-input" name="father_name" value={form.father_name} onChange={handleChange} placeholder="Father's full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Mother Name</label>
                  <input className="form-input" name="mother_name" value={form.mother_name} onChange={handleChange} placeholder="Mother's full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Spouse Name</label>
                  <input className="form-input" name="spouse_name" value={form.spouse_name} onChange={handleChange} placeholder="Spouse's name (if applicable)" />
                </div>
              </div>

              {/* ── Address ── */}
              <div className="form-section-title"><FaMapMarkerAlt size={12} /> Address Details</div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Full Address</label>
                <textarea className="form-textarea" name="full_address" value={form.full_address} onChange={handleChange} placeholder="Door no., Street, Area..." rows={2} />
              </div>
              <div className="form-grid-3" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Village</label>
                  <input className="form-input" name="village" value={form.village} onChange={handleChange} placeholder="Village name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Post</label>
                  <input className="form-input" name="post" value={form.post} onChange={handleChange} placeholder="Post office" />
                </div>
                <div className="form-group">
                  <label className="form-label">Hobli</label>
                  <input className="form-input" name="hobli" value={form.hobli} onChange={handleChange} placeholder="Hobli" />
                </div>
                <div className="form-group">
                  <label className="form-label">Taluk</label>
                  <input className="form-input" name="taluk" value={form.taluk} onChange={handleChange} placeholder="Taluk" />
                </div>
                <div className="form-group">
                  <label className="form-label">District</label>
                  <input className="form-input" name="district" value={form.district} onChange={handleChange} placeholder="District" />
                </div>
                <div className="form-group">
                  <label className="form-label">PIN Code</label>
                  <input className="form-input" name="pin" value={form.pin} onChange={handleChange} placeholder="6-digit PIN" maxLength={6} />
                </div>
              </div>

              {/* ── Contact ── */}
              <div className="form-section-title"><FaPhone size={12} /> Contact Information</div>
              <div className="form-grid-2" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Mobile Phone <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" name="mobile_phone" value={form.mobile_phone} onChange={handleChange} placeholder="10-digit mobile number" maxLength={10} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" name="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
                </div>
              </div>

              {/* ── Identity Documents ── */}
              <div className="form-section-title"><FaIdCard size={12} /> Identity Documents</div>
              <div className="form-grid-2" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Aadhaar No <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" name="aadhaar_no" value={form.aadhaar_no} onChange={handleChange} placeholder="12-digit Aadhaar number" maxLength={12} required />
                </div>
                <div className="form-group">
                  <label className="form-label">PAN</label>
                  <input className="form-input" name="pan" value={form.pan} onChange={handleChange} placeholder="PAN number" style={{ textTransform: 'uppercase' }} maxLength={10} />
                </div>
                <div className="form-group">
                  <label className="form-label">Voter ID</label>
                  <input className="form-input" name="voter_id" value={form.voter_id} onChange={handleChange} placeholder="Voter ID number" />
                </div>
                <div className="form-group">
                  <label className="form-label">Ration Card No</label>
                  <input className="form-input" name="ration_card_no" value={form.ration_card_no} onChange={handleChange} placeholder="Ration card number" />
                </div>
                <div className="form-group">
                  <label className="form-label">Driving License No</label>
                  <input className="form-input" name="driving_license_no" value={form.driving_license_no} onChange={handleChange} placeholder="Driving license number" />
                </div>
                <div className="form-group">
                  <label className="form-label">Passport No</label>
                  <input className="form-input" name="passport_no" value={form.passport_no} onChange={handleChange} placeholder="Passport number" />
                </div>
              </div>

              {/* ── Member Category ── */}
              <div className="form-section-title">Member Category</div>
              <div className="form-grid-2" style={{ marginBottom: 24 }}>
                <div className="form-group">
                  <label className="form-label">Member Category</label>
                  <input
                    type="text"
                    id="category-input"
                    className="form-input"
                    name="category"
                    list="category-options"
                    value={form.category}
                    onChange={handleChange}
                    onClick={(e) => {
                      try { if (e.target.showPicker) e.target.showPicker(); } catch (_) {}
                    }}
                    placeholder="Type or select member category..."
                    autoComplete="off"
                  />
                  <datalist id="category-options">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </datalist>
                </div>
              </div>

              {/* ── Photo & Signature ── */}
              <div className="form-section-title"><FaImage size={12} /> Photo & Signature</div>
              <div className="form-grid-2" style={{ marginBottom: 8 }}>
                {/* Photo Upload */}
                <div className="form-group">
                  <label className="form-label">Photo</label>
                  <div
                    className={`file-upload-box ${photoPreview ? 'has-file' : ''}`}
                    onClick={() => photoRef.current.click()}
                  >
                    {photoPreview ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <img src={photoPreview} alt="Preview" className="file-preview" />
                        <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Photo selected</span>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                        <div style={{ fontWeight: 600, color: '#374151', fontSize: 14 }}>Click to upload photo</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>JPG, PNG up to 5MB</div>
                      </div>
                    )}
                    <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
                  </div>
                </div>

                {/* Signature Upload */}
                <div className="form-group">
                  <label className="form-label">Signature</label>
                  <div
                    className={`file-upload-box ${signaturePreview ? 'has-file' : ''}`}
                    onClick={() => sigRef.current.click()}
                  >
                    {signaturePreview ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <img src={signaturePreview} alt="Signature" style={{ height: 60, objectFit: 'contain', borderRadius: 8, border: '1px solid #bbf7d0' }} />
                        <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Signature selected</span>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>✍️</div>
                        <div style={{ fontWeight: 600, color: '#374151', fontSize: 14 }}>Click to upload signature</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>JPG, PNG up to 5MB</div>
                      </div>
                    )}
                    <input ref={sigRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSignature} />
                  </div>
                </div>
              </div>

              {/* ── Submit ── */}
              <div style={{ display: 'flex', gap: 12, marginTop: 28, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ flex: 1, justifyContent: 'center' }}>
                  {loading ? (
                    <><div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Registering Member...</>
                  ) : (
                    <><FaSave size={15} /> Register Member</>
                  )}
                </button>
                <button type="button" className="btn btn-ghost btn-lg" onClick={handleReset}>
                  Reset Form
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
